import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { BookOpen, ChevronDown, ChevronRight, Users, Bell, Calendar, Settings, Shield, Search, MessageSquare, Activity, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import jsPDF from 'jspdf';
import signInPage from '@/assets/sign-in-page.png';
import clientProfileExample from '@/assets/client-profile-example.png';
import addNewClient from '@/assets/add_new_client.png';
import checkInConfirm from '@/assets/check-in-confirm.png';
import checkOutConfirm from '@/assets/check-out-confirm.png';
import staffAddAlert from '@/assets/staff-add-alert.png';
import adminAlertsSection from '@/assets/admin-alerts-section.png';
import adminSystemSettings from '@/assets/admin-system-settings.png';
import addNewStaffMember from '@/assets/add-new-staff-member.png';
import createRoster from '@/assets/create-roster.png';
import createBedGrid from '@/assets/create-bed-grid.png';
import printDailyCensus from '@/assets/print-daily-census.png';
import clientServicesLog from '@/assets/client-services-log.png';
import staffViewClient from '@/assets/staff-view-client.png';
import addClientService from '@/assets/add-client-service.png';
import checkOutScreen2 from '@/assets/check-out-screen-2.png';
import newClientForm2 from '@/assets/new-client-form-2.png';
import clientListTable from '@/assets/client-list-table.png';
import adminStaff from '@/assets/admin-staff.png';

interface UserGuideProps {
  compact?: boolean;
}

const UserGuide = ({ compact = false }: UserGuideProps) => {
  const [openSections, setOpenSections] = useState<string[]>(['overview']);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  const getScreenshotSrc = (screenshot: string) => {
    switch (screenshot) {
      case 'login': return signInPage;
      case 'client-profile': return clientProfileExample;
      case 'add-client': return addNewClient;
      case 'check-in': return checkInConfirm;
      case 'check-out': return checkOutConfirm;
      case 'add-alert': return staffAddAlert;
      case 'admin-alerts': return adminAlertsSection;
      case 'admin-settings': return adminSystemSettings;
      case 'staff-management': return addNewStaffMember;
      case 'roster-management': return createRoster;
      case 'bed-configuration': return createBedGrid;
      case 'print-census': return printDailyCensus;
      case 'client-services': return clientServicesLog;
      case 'staff-client-view': return staffViewClient;
      case 'add-service': return addClientService;
      case 'check-out-screen': return checkOutScreen2;
      case 'new-client-intake': return newClientForm2;
      case 'client-list-table': return clientListTable;
      case 'admin-staff': return adminStaff;
      default: return null;
    }
  };

  const guideContent = [
    {
      id: 'introduction',
      title: 'Introduction (Start Here)',
      icon: BookOpen,
      badge: 'Start Here',
      content: [
        {
          title: 'Welcome to Your Shelter Management System',
          description: 'This digital platform streamlines how shelters operate, replacing manual tracking with real-time visibility into client management, check-ins, alerts, and attendance reporting.',
          callout: { type: 'note', text: 'This system replaces manual tracking methods and provides real-time visibility into shelter operations.' }
        },
        {
          title: 'Who This Guide Serves',
          description: 'This guide covers the two primary roles in the system:',
          steps: [
            '• **Admin**: Full system access — manage users, clients, rooms, alerts, rosters, census, and settings',
            '• **Staff**: Day-to-day operations — check clients in/out, log services, view alerts, and manage client profiles'
          ],
          callout: { type: 'tip', text: 'Start with the Getting Started section, then focus on sections relevant to your role.' }
        }
      ]
    },
    {
      id: 'getting-started',
      title: 'Getting Started (Essential)',
      icon: Activity,
      badge: 'Essential',
      content: [
        {
          title: 'Account Setup & First Login',
          description: 'Initial steps to access your account.',
          steps: [
            '1. Receive login credentials from your administrator',
            '2. Navigate to the system URL in your web browser',
            '3. Enter your email address',
            '4. Enter your password',
            '5. Click "Login" to authenticate'
          ],
          callout: { type: 'warning', text: 'You may be prompted to change your password on first login for security.' },
          screenshot: 'login'
        },
        {
          title: 'System Requirements',
          description: 'Technical requirements for optimal system performance.',
          steps: [
            '• Modern web browser (Chrome, Firefox, Safari, Edge)',
            '• Stable internet connection',
            '• Screen resolution of 1024x768 or higher',
            '• JavaScript enabled in browser settings'
          ],
          callout: { type: 'tip', text: 'For best performance, use Chrome or Firefox with an up-to-date version.' }
        },
        {
          title: 'User Roles & Access Levels',
          description: 'The system uses two primary roles:',
          steps: [
            '**Admin**',
            '• Full access to all system features',
            '• User management: create staff accounts, reset passwords, assign roles',
            '• Client management: full CRUD, intake, roster management',
            '• Room & bed configuration',
            '• Alert creation and management',
            '• Census and attendance reports',
            '• System settings',
            '',
            '**Staff**',
            '• Check-in / check-out clients',
            '• View and manage client profiles',
            '• Log services (meals, metro passes, laundry, etc.)',
            '• View active alerts (read-only)',
            '• View daily census'
          ],
          callout: { type: 'note', text: 'Your role determines which features you can access. Contact your administrator if you need different permissions.' }
        },
        {
          title: 'Access Matrix',
          description: 'Quick reference for feature access by role.',
          steps: [
            '**User Management** — Admin: ✅ | Staff: ❌',
            '**Password Resets** — Admin: ✅ | Staff: ❌',
            '**Client Management** — Admin: ✅ Full | Staff: ✅ Limited',
            '**Check-In/Check-Out** — Admin: ✅ | Staff: ✅',
            '**Service Tracking** — Admin: ✅ | Staff: ✅',
            '**Alerts** — Admin: ✅ Create/Manage | Staff: ✅ View only',
            '**Roster Management** — Admin: ✅ | Staff: ❌',
            '**Daily Census** — Admin: ✅ Full | Staff: ✅ View only',
            '**Room Configuration** — Admin: ✅ | Staff: ❌',
            '**System Settings** — Admin: ✅ | Staff: ❌'
          ],
          callout: { type: 'tip', text: 'Use this matrix to quickly understand what features are available to your role.' }
        },
        {
          title: 'Room Configuration',
          description: 'Setting up bed assignments and room management (Admin only).',
          steps: [
            '1. Navigate to "Roster Management" from admin dashboard',
            '2. Click settings icon to access "Room Configuration"',
            '3. Set total number of rooms available',
            '4. Configure room prefixes (A, B, C, etc.)',
            '5. Define rooms per floor for organization',
            '6. Enable sub-rooms if needed (a, b designations)',
            '7. Click "Save Settings" to apply configuration'
          ],
          callout: { type: 'tip', text: 'Room configuration affects bed availability calculations and assignment logic.' },
          screenshot: 'bed-configuration'
        }
      ]
    },
    {
      id: 'client-management',
      title: 'Client Management',
      icon: Users,
      badge: 'Core Feature',
      content: [
        {
          title: 'Client Management Overview',
          description: 'The Client Management system handles the full client lifecycle — from intake through daily tracking and updates.',
          callout: { type: 'note', text: 'Accurate client records ensure seamless care coordination and compliance.' }
        },
        {
          title: '1. Client Intake Process',
          description: 'Add new clients to the system and assign them rooms.',
          steps: [
            '1. Go to Roster Management and click **+ New Client**',
            '2. Enter basic information (name, date of birth, contact details)',
            '3. Record address and emergency contacts',
            '4. Assign a room and bed from available slots',
            '5. Document any food allergies or medical alerts',
            '6. Upload intake photos for identification',
            '7. Click **Save**',
            '8. Optionally check the client in immediately after saving'
          ],
          callout: { type: 'tip', text: 'Intake photos greatly improve identification accuracy during daily operations.' },
          screenshot: 'new-client-intake'
        },
        {
          title: '2. Client List & Profiles',
          description: 'View and manage all clients in a searchable table with real-time status indicators.',
          steps: [
            '1. View the Client List with columns for Name, Status, Facility, and Room',
            '2. Use the search bar to find clients by name',
            '3. Filter by site/facility and status',
            '4. Click any client row to view their detailed profile',
            '5. Update personal or contact information as needed',
            '6. Review check-in/check-out history',
            '7. Monitor service usage'
          ],
          callout: { type: 'note', text: 'Click any row to access the full client profile with history and services.' },
          screenshot: 'client-list-table'
        }
      ]
    },
    {
      id: 'daily-operations',
      title: 'Daily Operations',
      icon: Calendar,
      badge: 'Essential',
      content: [
        {
          title: 'Daily Operations Overview',
          description: 'Daily operations cover client arrivals and departures, service delivery, and bed management.',
          callout: { type: 'note', text: 'Efficient daily operations ensure smooth shelter functioning.' }
        },
        {
          title: '1. Client Check-In',
          description: 'Record client arrivals and assign beds.',
          steps: [
            '1. From the roster, locate the client by name or room',
            '2. Click the **Check In** button',
            '3. Confirm or manually select a bed',
            '4. Add arrival notes if needed',
            '5. Click **Confirm Check-In**'
          ],
          callout: { type: 'tip', text: 'Always verify client identity before processing check-in.' },
          screenshot: 'check-in'
        },
        {
          title: '2. Client Check-Out',
          description: 'Track when clients leave and free up beds.',
          steps: [
            '1. From the roster, locate the client',
            '2. Click the **Check Out** button',
            '3. Select departure reason',
            '4. Add notes about client status',
            '5. Click **Confirm Check Out**',
            '6. Bed automatically becomes available'
          ],
          callout: { type: 'warning', text: 'Complete check-out logs for accurate tracking and reporting.' },
          screenshot: 'check-out'
        },
        {
          title: '3. Bed Management',
          description: 'Real-time bed tracking with color-coded status indicators.',
          steps: [
            '1. Access **Roster Management** from the dashboard',
            '2. View the bed grid: Green = Occupied, White = Available, Red = Out of Service',
            '3. Click bed numbers for assignment details',
            '4. Use **Manual Assignment** for specific requests',
            '5. Update bed status for maintenance or cleaning'
          ],
          callout: { type: 'tip', text: 'Regular bed status updates ensure accurate capacity reporting.' },
          screenshot: 'roster-management'
        },
        {
          title: '4. Service Tracking',
          description: 'Record services provided to clients (meals, metro passes, laundry, etc.).',
          steps: [
            '1. Open a client profile from the roster',
            '2. Navigate to the **Services** tab',
            '3. Click **Add Service** to record a new entry',
            '4. Select service type and quantity',
            '5. Review recent services in chronological order'
          ],
          callout: { type: 'note', text: 'Service tracking helps monitor client engagement and identify needs.' },
          screenshot: 'client-services'
        },
        {
          title: '5. Daily Census & Reporting',
          description: 'Generate daily reports and manage occupancy data.',
          steps: [
            '1. Select "Roster Management" for the current census',
            '2. Review client status and room assignments',
            '3. Click "Print" to generate physical reports',
            '4. Export CSV for attendance reporting'
          ],
          callout: { type: 'tip', text: 'Print daily census reports for backup documentation and compliance.' },
          screenshot: 'print-census'
        }
      ]
    },
    {
      id: 'alerts',
      title: 'Alerts',
      icon: Bell,
      badge: 'Important',
      content: [
        {
          title: 'Alert Management',
          description: 'Create and manage alerts for staff notifications (Admin only can create; Staff can view).',
          steps: [
            '1. Navigate to "Alerts" from the main menu',
            '2. Click "Add Alert" to create a new notification (Admin)',
            '3. Select priority: Low, Medium, or High',
            '4. Choose type: Staff, Service, or Building',
            '5. Enter title and description',
            '6. Optionally associate with a specific client',
            '7. Click "Create Alert" to notify staff'
          ],
          callout: { type: 'note', text: 'High priority alerts send immediate notifications to all staff members.' },
          screenshot: 'add-alert'
        }
      ]
    },
    {
      id: 'admin-features',
      title: 'Admin Features',
      icon: Settings,
      badge: 'Admin Only',
      content: [
        {
          title: 'User Management',
          description: 'Create and manage staff accounts, roles, and access.',
          steps: [
            '1. Access "User Management" from the admin dashboard',
            '2. View all users with roles and facility assignments',
            '3. Click "Add Staff Member" to create a new account',
            '4. Set email, role (Admin or Staff), and facility assignment',
            '5. Set initial login credentials',
            '6. Reset passwords for existing users as needed'
          ],
          callout: { type: 'warning', text: 'Only administrators can manage staff accounts and permissions.' },
          screenshot: 'admin-staff'
        },
        {
          title: 'System Settings',
          description: 'Configure system-wide options.',
          steps: [
            '1. Navigate to System Settings from admin panel',
            '2. Configure bed capacity and room assignments',
            '3. Set up alert thresholds and notifications',
            '4. Manage service types and tracking options'
          ],
          callout: { type: 'tip', text: 'Changes to system settings affect all users and should be tested carefully.' },
          screenshot: 'admin-settings'
        }
      ]
    },
    {
      id: 'emergency-procedures',
      title: 'Emergency Procedures',
      icon: Bell,
      badge: 'Emergency',
      content: [
        {
          title: 'Critical Incident Response',
          description: 'Procedures for handling emergency situations.',
          steps: [
            '1. Ensure immediate safety of all persons',
            '2. Contact emergency services if required (911)',
            '3. Notify supervisor and facility management',
            '4. Document incident details in system',
            '5. Create high-priority alert for all staff',
            '6. Follow up with incident report documentation'
          ],
          callout: { type: 'warning', text: 'Always prioritize safety first. System documentation can wait until the emergency is resolved.' }
        },
        {
          title: 'Emergency Workflow Guides',
          description: 'Quick reference for common emergency scenarios.',
          steps: [
            '- **Medical Emergency** - Call 911, notify supervisor, document incident',
            '- **Fire Emergency** - Evacuate building, call 911, account for all clients',
            '- **Security Incident** - Ensure safety, call authorities, create incident alert',
            '- **System Outage** - Use backup paper forms, notify IT support immediately',
            '- **Severe Weather** - Follow facility emergency plan, update client status'
          ],
          callout: { type: 'note', text: 'Keep emergency contact numbers readily available outside the digital system.' }
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: MessageSquare,
      badge: 'Help',
      content: [
        {
          title: 'Common Errors',
          description: 'Frequently encountered issues and their solutions.',
          steps: [
            '- **Q: "Login Failed" Error** - Check email/password, ensure caps lock is off, try clearing browser cache',
            '',
            '- **Q: Client not showing in roster** - Check if client was checked out, use search function, refresh page',
            '',
            '- **Q: Bed assignment shows as unavailable** - Verify bed status in roster management, check for maintenance mode',
            '',
            '- **Q: System running slowly** - Close other browser tabs, check internet connection, contact IT if persistent'
          ],
          callout: { type: 'tip', text: 'Most display issues can be resolved by refreshing your browser page (F5 or Ctrl+R).' }
        },
        {
          title: 'Quick Fixes',
          description: 'Simple solutions for common problems.',
          steps: [
            '- **Page won\'t load** - Refresh browser, check internet connection',
            '- **Data not saving** - Ensure all required fields are filled',
            '- **Search not working** - Clear search terms and try again',
            '- **Wrong information showing** - Log out and log back in',
            '- **Print function not working** - Check browser print settings'
          ],
          callout: { type: 'warning', text: 'If issues persist after trying quick fixes, document the problem and contact support immediately.' }
        },
        {
          title: 'Contacting Support',
          description: 'When and how to get additional help.',
          steps: [
            '1. Try the quick fixes listed above first',
            '2. Document what you were doing when the problem occurred',
            '3. Note any error messages exactly as they appear',
            '4. Contact your shift supervisor for immediate issues',
            '5. For technical problems, email IT support with details'
          ],
          callout: { type: 'note', text: 'Emergency shelter operations should never be interrupted by system issues. Use backup paper forms if needed.' }
        }
      ]
    },
    {
      id: 'security-privacy',
      title: 'Security & Privacy',
      icon: Shield,
      badge: 'Security',
      content: [
        {
          title: 'Data Protection Guidelines',
          description: 'Best practices for maintaining client confidentiality and data security.',
          steps: [
            '1. Never share login credentials with other staff members',
            '2. Log out when leaving workstation unattended',
            '3. Ensure client information is only shared on need-to-know basis',
            '4. Use secure networks only — avoid public WiFi for system access',
            '5. Report any suspected security breaches immediately',
            '6. Keep client records confidential and secure'
          ],
          callout: { type: 'warning', text: 'Unauthorized access to client information may result in legal consequences and disciplinary action.' }
        },
        {
          title: 'Role-Based Access',
          description: 'Understanding permission levels and access controls.',
          steps: [
            '- **Admin** - Full system access including user management, settings, and all operational features',
            '- **Staff** - Client check-in/out, service tracking, profile viewing, and alert viewing'
          ],
          callout: { type: 'note', text: 'Your access level is determined by your assigned role. Contact your administrator to request changes.' }
        }
      ]
    },
    {
      id: 'glossary',
      title: 'Glossary',
      icon: Search,
      badge: 'Reference',
      content: [
        {
          title: 'Key Terms & Definitions',
          description: 'Common terms used throughout the shelter management system.',
          steps: [
            '- **Bed Assignment** - Process of allocating specific sleeping spaces to clients',
            '- **Capacity** - Total number of available beds or current occupancy percentage',
            '- **Check-In** - Process of registering a client\'s arrival and bed assignment',
            '- **Check-Out** - Process of registering a client\'s departure and freeing their bed',
            '- **Census** - Daily count of clients currently in the shelter',
            '- **Dashboard** - Main overview screen showing key system information and metrics',
            '- **Intake** - Initial process of registering a new client in the system',
            '- **Occupancy** - Current number of beds in use versus total capacity',
            '- **Roster** - Complete list of all clients currently in the system',
            '- **User Role** - Permission level that determines which features a user can access'
          ],
          callout: { type: 'note', text: 'Bookmark this glossary for quick reference when training new staff members.' }
        }
      ]
    }
  ];

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return lines.length * (fontSize * 0.35) + 2;
      };

      // Title Page
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Shelter Management System', pageWidth / 2, 60, { align: 'center' });
      
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.text('User Guide', pageWidth / 2, 80, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 100, { align: 'center' });

      // Table of Contents
      pdf.addPage();
      yPosition = margin;
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Table of Contents', margin, yPosition);
      yPosition += 20;

      guideContent.forEach((section, index) => {
        checkNewPage(10);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${index + 1}. ${section.title}`, margin + 5, yPosition);
        const pageNum = 3 + (index * 2);
        pdf.text(`${pageNum}`, pageWidth - margin - 20, yPosition);
        yPosition += 8;
      });

      // Process each section
      for (let sectionIndex = 0; sectionIndex < guideContent.length; sectionIndex++) {
        const section = guideContent[sectionIndex];
        pdf.addPage();
        yPosition = margin;
        
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${sectionIndex + 1}. ${section.title}`, margin, yPosition);
        yPosition += 15;

        if (section.badge) {
          pdf.setFillColor(59, 130, 246);
          pdf.roundedRect(margin, yPosition, 30, 8, 2, 2, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          pdf.text(section.badge, margin + 15, yPosition + 5.5, { align: 'center' });
          pdf.setTextColor(0, 0, 0);
          yPosition += 15;
        }

        for (let contentIndex = 0; contentIndex < section.content.length; contentIndex++) {
          const content = section.content[contentIndex];
          
          checkNewPage(30);
          
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          const titleHeight = addWrappedText(content.title, margin, yPosition, contentWidth, 16);
          yPosition += titleHeight + 5;

          if (content.description) {
            checkNewPage(20);
            pdf.setFont('helvetica', 'normal');
            const descHeight = addWrappedText(content.description, margin, yPosition, contentWidth, 11);
            yPosition += descHeight + 8;
          }

          if (content.steps) {
            checkNewPage(20);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Steps:', margin, yPosition);
            yPosition += 8;

            content.steps.forEach((step: string, stepIndex: number) => {
              checkNewPage(15);
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(10);
              
              const cleanStep = step.replace(/\*\*(.*?)\*\*/g, '$1').replace(/^\d+\.\s*/, '').replace(/^•\s*/, '');
              const stepNumber = step.match(/^\d+\./) ? '' : `${stepIndex + 1}. `;
              const fullStep = stepNumber + cleanStep;
              
              const stepHeight = addWrappedText(fullStep, margin + 5, yPosition, contentWidth - 10, 10);
              yPosition += stepHeight + 3;
            });
            yPosition += 5;
          }

          if (content.callout) {
            checkNewPage(25);
            
            const calloutColor = content.callout.type === 'warning' ? [255, 243, 205] : 
                               content.callout.type === 'tip' ? [219, 234, 254] : [243, 244, 246];
            pdf.setFillColor(calloutColor[0], calloutColor[1], calloutColor[2]);
            
            const calloutHeight = 20;
            pdf.roundedRect(margin, yPosition, contentWidth, calloutHeight, 3, 3, 'F');
            
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const calloutTitle = content.callout.type.charAt(0).toUpperCase() + content.callout.type.slice(1) + ':';
            pdf.text(calloutTitle, margin + 5, yPosition + 8);
            
            pdf.setFont('helvetica', 'normal');
            const textHeight = addWrappedText(content.callout.text, margin + 5, yPosition + 14, contentWidth - 10, 9);
            yPosition += Math.max(calloutHeight, textHeight + 10) + 5;
          }

          if ('screenshot' in content && content.screenshot) {
            const screenshots = Array.isArray(content.screenshot) ? content.screenshot : [content.screenshot];
            
            for (const screenshot of screenshots) {
              const screenshotSrc = getScreenshotSrc(screenshot);
              if (screenshotSrc) {
                try {
                  checkNewPage(80);
                  
                  const img = document.createElement('img');
                  img.src = screenshotSrc;
                  await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = reject;
                    setTimeout(reject, 5000);
                  });

                  const maxWidth = contentWidth - 20;
                  const maxHeight = 80;
                  const aspectRatio = img.width / img.height;
                  
                  let imgWidth = Math.min(maxWidth, img.width * 0.4);
                  let imgHeight = imgWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = imgHeight * aspectRatio;
                  }

                  pdf.setFontSize(10);
                  pdf.setFont('helvetica', 'italic');
                  pdf.text(`Screenshot: ${screenshot}`, margin + 10, yPosition);
                  yPosition += 8;
                  
                  pdf.addImage(screenshotSrc, 'PNG', margin + 10, yPosition, imgWidth, imgHeight);
                  yPosition += imgHeight + 15;
                  
                } catch (error) {
                  console.warn('Failed to add screenshot:', error);
                  pdf.setFontSize(10);
                  pdf.setFont('helvetica', 'italic');
                  pdf.text('[Screenshot: ' + screenshot + ']', margin + 10, yPosition);
                  yPosition += 10;
                }
              }
            }
          }

          yPosition += 10;
        }
      }

      const totalPages = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        pdf.text('Shelter Management System - User Guide', margin, pageHeight - 10);
      }

      pdf.save('shelter-management-guide.pdf');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className={cn("max-w-4xl mx-auto space-y-4", compact ? "p-4" : "p-6")}>
      {!compact && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">User Manual</h1>
            <p className="text-sm text-muted-foreground">
              Complete guide for using the Shelter Management System
            </p>
          </div>
          <Button onClick={generatePDF} disabled={isGeneratingPDF} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      )}

      <div ref={printRef} className={cn("space-y-4", compact ? "text-sm" : "space-y-6")}>
        {!compact && (
          <div className="text-center space-y-3 no-print">
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              Follow these instructions to effectively use all features of the shelter management system.
            </p>
          </div>
        )}

        {guideContent.map(section => (
          <Card key={section.id} className="overflow-hidden">
            <CardHeader className={cn("pb-2", compact && "py-2")}>
              <Collapsible open={openSections.includes(section.id)} onOpenChange={() => toggleSection(section.id)}>
                <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-primary transition-colors">
                  <div className="flex items-center gap-2">
                    <section.icon className={cn("text-primary", compact ? "h-4 w-4" : "h-5 w-5")} />
                    <CardTitle className={cn("font-semibold", compact ? "text-sm" : "text-lg")}>{section.title}</CardTitle>
                    <Badge variant="secondary" className={cn("ml-1", compact ? "text-xs px-1.5 py-0.5" : "text-xs")}>
                      {section.badge}
                    </Badge>
                  </div>
                  {openSections.includes(section.id) ? (
                    <ChevronDown className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                  ) : (
                    <ChevronRight className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                  )}
                </CollapsibleTrigger>

                <CollapsibleContent className={cn(compact ? "pt-2" : "pt-4")}>
                  <CardContent className={cn("pt-0", compact ? "space-y-3 pb-3" : "space-y-6")}>
                    {section.content.map((item, index) => (
                      <div key={index} className={cn(compact ? "space-y-2" : "space-y-4")}>
                        <div>
                          <h4 className={cn("font-semibold text-foreground mb-1", compact ? "text-sm" : "text-base")}>{item.title}</h4>
                          <p className={cn("text-muted-foreground mb-2", compact ? "text-xs" : "text-sm")}>{item.description}</p>
                          
                          {item.steps && (
                            <div className={cn(compact ? "space-y-1" : "space-y-2")}>
                              {item.steps.map((step, stepIndex) => (
                                <div key={stepIndex} className={cn(compact ? "text-xs" : "text-sm")}>
                                  {step.startsWith('- **') ? (
                                    <p className="text-muted-foreground leading-relaxed">
                                      <span className="font-semibold text-foreground">{step.match(/\*\*(.*?)\*\*/)?.[1]}</span>
                                      {step.replace(/^- \*\*.*?\*\*/, '')}
                                    </p>
                                  ) : step.startsWith('**') ? (
                                    <p className="text-muted-foreground leading-relaxed">
                                      <span className="font-semibold text-foreground">{step.match(/\*\*(.*?)\*\*/)?.[1]}</span>
                                      {step.replace(/\*\*.*?\*\*:?\s*/, '')}
                                    </p>
                                  ) : step.startsWith('•') ? (
                                    <div className="flex items-start gap-2">
                                      <span className="text-primary mt-1.5 text-xs">•</span>
                                      <span className="text-muted-foreground leading-relaxed">
                                        {step.replace(/^•\s*/, '').split(/\*\*(.*?)\*\*/).map((part, i) =>
                                          i % 2 === 1 ? <span key={i} className="font-semibold text-foreground">{part}</span> : part
                                        )}
                                      </span>
                                    </div>
                                  ) : step.trim() === '' ? (
                                    <div className="h-2"></div>
                                  ) : (
                                    <div className="flex items-start gap-2">
                                      {step.match(/^\d+\./) && (
                                        <span className={cn("inline-flex items-center justify-center rounded-full bg-primary/10 text-primary font-medium flex-shrink-0 mt-0.5", compact ? "w-4 h-4 text-xs" : "w-6 h-6 text-xs")}>
                                          {stepIndex + 1}
                                        </span>
                                      )}
                                      <span className="text-muted-foreground leading-relaxed">
                                        {step.replace(/^\d+\.\s*/, '').split(/\*\*(.*?)\*\*/).map((part, i) =>
                                          i % 2 === 1 ? <span key={i} className="font-semibold text-foreground">{part}</span> : part
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {item.callout && (
                            <div className={cn(
                              "rounded-lg p-3 border-l-4 mt-3",
                              compact ? "text-xs" : "text-sm",
                              item.callout.type === 'tip' && "bg-blue-50 border-blue-400 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
                              item.callout.type === 'warning' && "bg-yellow-50 border-yellow-400 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200",
                              item.callout.type === 'note' && "bg-green-50 border-green-400 text-green-800 dark:bg-green-950/30 dark:text-green-200"
                            )}>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold capitalize">{item.callout.type}:</span>
                                <span>{item.callout.text}</span>
                              </div>
                            </div>
                          )}

                          {'screenshot' in item && item.screenshot && (
                            <div className={cn("mt-4 rounded-lg overflow-hidden border", compact ? "max-w-sm" : "max-w-2xl")}>
                              {(() => {
                                const src = getScreenshotSrc(item.screenshot as string);
                                return src ? (
                                  <img
                                    src={src}
                                    alt={item.title}
                                    className="w-full h-auto"
                                    loading="lazy"
                                  />
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UserGuide;
